import React, { useState, useMemo, lazy, Suspense } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  styled,
  Chip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  useTheme,
} from "@mui/material";
import {
  PictureAsPdf as PdfIcon,
  Audiotrack as AudioIcon,
  CalendarToday as CalendarIcon,
  MenuBook as MenuBookIcon,
  Person as PersonIcon,
  LocalOffer as LocalOfferIcon,
  PlayArrow as PlayArrowIcon,
  YouTube as YouTubeIcon,
  Close as CloseIcon,
  Launch as LaunchIcon,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import LazyLoad from "react-lazyload";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Lazy load readers for better performance
const PDFReader = lazy(() => import("../readers/PDFReader"));
const AudioPlayer = lazy(() => import("../readers/AudioPlayer"));

const StyledCard = styled(Card)({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  maxWidth: "280px",
  backgroundColor: "#fff",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  borderRadius: "4px",
  overflow: "hidden",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  },
});

const BookCover = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
  backgroundColor: "#f8f9fa",
  transition: "opacity 0.3s ease-in-out",
});

const CardContentStyled = styled(CardContent)(({ isRtl }) => ({
  padding: "8px 12px",
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  textAlign: isRtl ? 'right' : 'left',
}));

const BookTitle = styled(Typography)(({ isRtl }) => ({
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "#333",
  lineHeight: 1.2,
  marginBottom: "2px",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  height: "2.4em",
  transition: "color 0.2s ease",
  textAlign: isRtl ? 'right' : 'left',
  direction: isRtl ? 'rtl' : 'ltr',
  "&:hover": {
    color: "#0066cc",
  },
}));

const BookDescription = styled(Typography)(({ isRtl }) => ({
  fontSize: "0.75rem",
  color: "#666",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  marginBottom: "8px",
  lineHeight: 1.3,
  textAlign: isRtl ? 'right' : 'left',
  direction: isRtl ? 'rtl' : 'ltr',
}));

const AccessButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  textTransform: "none",
  fontWeight: 600,
  padding: "4px 12px",
  fontSize: "0.75rem",
  borderRadius: "4px",
  minHeight: "28px",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const FormatChip = styled(Chip)(({ theme, isRtl }) => ({
  position: "absolute",
  top: 8,
  left: isRtl ? 'auto' : 8,
  right: isRtl ? 8 : 'auto',
  height: "24px",
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  backdropFilter: "blur(4px)",
  "& .MuiChip-icon": {
    fontSize: "1rem",
    marginLeft: "4px",
  },
}));

const FreeChip = styled(Chip)(({ theme, isRtl }) => ({
  position: "absolute",
  top: 8,
  right: isRtl ? 'auto' : 8,
  left: isRtl ? 8 : 'auto',
  height: "24px",
  backgroundColor: theme.palette.success.main,
  color: theme.palette.common.white,
  fontWeight: 600,
  "& .MuiChip-icon": {
    fontSize: "1rem",
    color: theme.palette.common.white,
  },
}));

const MetadataContainer = styled(Box)(({ theme, isRtl }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  marginBottom: "8px",
  textAlign: isRtl ? 'right' : 'left',
}));

const MetadataItem = styled(Box)(({ theme, isRtl }) => ({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  color: theme.palette.text.secondary,
  flexDirection: isRtl ? 'row-reverse' : 'row',
  "& .MuiSvgIcon-root": {
    fontSize: "1rem",
  },
}));

const MetadataText = styled(Typography)(({ isRtl }) => ({
  fontSize: "0.75rem",
  lineHeight: 1.2,
  display: "flex",
  alignItems: "center",
  textAlign: isRtl ? 'right' : 'left',
}));

// New components for improved image handling
const ImagePlaceholder = styled(Skeleton)(({ theme }) => ({
  width: "100%",
  height: "100%",
  position: "absolute",
  top: 0,
  left: 0,
  transform: "none",
  transformOrigin: "0 0",
}));

const LazyBookCover = styled(Box)(({ theme }) => ({
  position: "relative",
  width: "100%",
  height: 200,
  backgroundColor: theme.palette.grey[50],
  overflow: "hidden",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const BookCoverPlaceholder = styled(Box)(({ theme }) => ({
  width: "100%",
  height: 200,
  backgroundColor: theme.palette.grey[100],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.text.secondary,
  fontSize: "0.875rem",
}));

// New BookCoverImage component
const BookCoverImage = ({ src, alt, book }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const { t } = useTranslation();

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setError(true);
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Link to={`/books/${book.id}`} style={{ textDecoration: 'none' }}>
        <LazyLoad
          height={200}
          offset={100}
          resize={true}
          scroll={true}
          placeholder={
            <BookCoverPlaceholder>
              <Box sx={{ textAlign: "center", p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {t('freeBookCard.loading')}
                </Typography>
              </Box>
            </BookCoverPlaceholder>
          }
        >
          <LazyBookCover>
            {isLoading && (
              <ImagePlaceholder variant="rectangular" animation="wave" />
            )}
            <BookCover
              src={src}
              alt={alt}
              loading="lazy"
              onLoad={handleLoad}
              onError={handleError}
              style={{
                display: error ? "none" : "block",
                opacity: isLoading ? 0 : 1,
              }}
            />
            {error && (
              <BookCoverPlaceholder>
                <Box sx={{ textAlign: "center", p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('freeBookCard.imageNotAvailable')}
                  </Typography>
                </Box>
              </BookCoverPlaceholder>
            )}
          </LazyBookCover>
        </LazyLoad>
      </Link>
    </Box>
  );
};

const YouTubeDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '100%',
    maxWidth: 500,
    borderRadius: theme.spacing(1),
  },
}));

const YouTubeListItem = styled(ListItem)(({ theme }) => ({
  margin: theme.spacing(1, 0),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const FreeBookCard = ({ book }) => {
  const [youtubeLinksOpen, setYoutubeLinksOpen] = useState(false);
  const [readerOpen, setReaderOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  
  // Check if the language is RTL (Pashto or Dari)
  const isRtl = i18n.language === 'ps' || i18n.language === 'dr';
  
  // Check if the book is an audio book with YouTube links
  const hasYoutubeLinks = useMemo(() => {
    if (!book || book.format !== "Audiobook") return false;
    return book.collections?.some(collection => !!collection.youtube_link);
  }, [book]);

  const isPDF = useMemo(() => {
    return book && book.format === "PDF";
  }, [book]);

  const isAudio = useMemo(() => {
    return book && book.format === "Audiobook" && !hasYoutubeLinks;
  }, [book, hasYoutubeLinks]);

  if (!book) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "en" ? "en-US" : i18n.language === "ps" ? "ps-AF" : "fa-AF", {
      month: "short",
      year: "numeric",
    });
  };

  const getAuthorNames = () => {
    if (book.added_by_display && book.added_by_display.length > 0) {
      return book.added_by_display.join(", ");
    }
    return t('freeBookCard.unknownAuthor');
  };

  const shortDescription = book.description
    ? book.description
    : t('freeBookCard.defaultDescription');
    
  const handlePlayClick = () => {
    if (hasYoutubeLinks) {
      setYoutubeLinksOpen(true);
    } else if (isPDF || isAudio) {
      // For PDF and non-YouTube audio books
      if (book.collections && book.collections.length > 0) {
        setSelectedCollection(book.collections[0]);
        setReaderOpen(true);
      }
    }
  };
  
  const handleYoutubeClick = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleCloseReader = () => {
    setReaderOpen(false);
    setSelectedCollection(null);
  };

  const getFormatLabel = () => {
    if (hasYoutubeLinks) return t('freeBookCard.formats.youtube');
    if (book.format === "PDF") return t('freeBookCard.formats.pdf');
    return t('freeBookCard.formats.audiobook');
  };

  return (
    <>
      <StyledCard>
        <Box sx={{ position: "relative" }}>
          <BookCoverImage
            src={book.cover_image}
            alt={book.title}
            book={book}
          />

          <FormatChip
            icon={book.format === "PDF" ? <PdfIcon /> : hasYoutubeLinks ? <YouTubeIcon /> : <AudioIcon />}
            label={getFormatLabel()}
            size="small"
            isRtl={isRtl}
          />

          <FreeChip 
            icon={<LocalOfferIcon />} 
            label={t('freeBookCard.free')} 
            size="small" 
            isRtl={isRtl}
          />
        </Box>

        <CardContentStyled isRtl={isRtl}>
          <Box>
            <Link to={`/books/${book.id}`} style={{ textDecoration: 'none' }}>
              <BookTitle isRtl={isRtl}>{book.title}</BookTitle>
            </Link>

            <MetadataContainer isRtl={isRtl}>
              <MetadataItem isRtl={isRtl}>
                <PersonIcon />
                <MetadataText isRtl={isRtl}>{getAuthorNames()}</MetadataText>
              </MetadataItem>

              <MetadataItem isRtl={isRtl}>
                <CalendarIcon />
                <MetadataText isRtl={isRtl}>{formatDate(book.publication_date)}</MetadataText>
              </MetadataItem>

              <MetadataItem isRtl={isRtl}>
                <MenuBookIcon />
                <MetadataText isRtl={isRtl}>{book.pages} {t('freeBookCard.pages')}</MetadataText>
              </MetadataItem>
            </MetadataContainer>
          </Box>

          <BookDescription isRtl={isRtl}>{shortDescription}</BookDescription>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <AccessButton
              fullWidth
              variant="contained"
              size="small"
              onClick={handlePlayClick}
              startIcon={book.format === "Audiobook" ? <PlayArrowIcon /> : <PdfIcon />}
            >
              {book.format === "Audiobook" ? t('freeBookCard.actions.play') : t('freeBookCard.actions.read')}
            </AccessButton>
          </Box>
        </CardContentStyled>
      </StyledCard>
      
      {/* YouTube Links Dialog */}
      <YouTubeDialog
        open={youtubeLinksOpen}
        onClose={() => setYoutubeLinksOpen(false)}
        aria-labelledby="youtube-dialog-title"
      >
        <DialogTitle id="youtube-dialog-title" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div" sx={{ textAlign: isRtl ? 'right' : 'left', width: '100%' }}>
            {t('freeBookCard.dialog.title', { bookTitle: book.title })}
          </Typography>
          <IconButton 
            aria-label="close" 
            onClick={() => setYoutubeLinksOpen(false)} 
            size="small"
            sx={{ ml: isRtl ? 'auto' : 0, mr: isRtl ? 0 : 'auto' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <List dir={isRtl ? 'rtl' : 'ltr'}>
            {book.collections?.filter(c => c.youtube_link).map((collection, index) => (
              <YouTubeListItem 
                button 
                key={collection.id || index} 
                onClick={() => handleYoutubeClick(collection.youtube_link)}
                sx={{ flexDirection: isRtl ? 'row-reverse' : 'row' }}
              >
                <ListItemIcon sx={{ minWidth: 36, mr: isRtl ? 0 : 2, ml: isRtl ? 2 : 0 }}>
                  <YouTubeIcon color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary={collection.filename || t('freeBookCard.dialog.video', { number: index + 1 })} 
                  secondary={collection.youtube_link}
                  primaryTypographyProps={{ 
                    fontWeight: 500,
                    textAlign: isRtl ? 'right' : 'left'
                  }}
                  secondaryTypographyProps={{ 
                    noWrap: true,
                    textAlign: isRtl ? 'right' : 'left',
                    sx: { maxWidth: '220px' }
                  }}
                />
                <LaunchIcon fontSize="small" color="action" />
              </YouTubeListItem>
            ))}
            {(!book.collections || book.collections.filter(c => c.youtube_link).length === 0) && (
              <Typography color="text.secondary" align="center" sx={{ py: 2 }}>
                {t('freeBookCard.dialog.noVideos')}
              </Typography>
            )}
          </List>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', p: 2 }}>
          <Button 
            onClick={() => setYoutubeLinksOpen(false)} 
            variant="outlined"
          >
            {t('freeBookCard.dialog.close')}
          </Button>
        </DialogActions>
      </YouTubeDialog>

      {/* PDF/Audio Reader */}
      <Suspense fallback={<Dialog fullScreen open={readerOpen}><Box sx={{ p: 4, textAlign: 'center' }}>Loading...</Box></Dialog>}>
        <Dialog
          fullScreen
          open={readerOpen}
          onClose={handleCloseReader}
        >
          {selectedCollection && isPDF ? (
            <PDFReader
              url={`${process.env.REACT_APP_API_URL || ''}/books/${book.id}/read/?collection_id=${selectedCollection.id}`}
              onClose={handleCloseReader}
            />
          ) : selectedCollection && isAudio ? (
            <AudioPlayer
              url={`${process.env.REACT_APP_API_URL || ''}/books/${book.id}/read/?collection_id=${selectedCollection.id}`}
              title={book.title}
              author={getAuthorNames()}
              onClose={handleCloseReader}
            />
          ) : null}
        </Dialog>
      </Suspense>
    </>
  );
};

BookCoverImage.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  book: PropTypes.object.isRequired,
};

FreeBookCard.propTypes = {
  book: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    cover_image: PropTypes.string.isRequired,
    authors_display: PropTypes.arrayOf(PropTypes.string),
    publication_date: PropTypes.string.isRequired,
    pages: PropTypes.number.isRequired,
    description: PropTypes.string,
    format: PropTypes.string,
    collections: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        filename: PropTypes.string,
        youtube_link: PropTypes.string,
      }),
    ),
  }),
};

export default FreeBookCard;
